import { useEffect, useState } from "react";
import { fetchScopes, fetchSources, saveScopes } from "../api/client";
import { Alert } from "../components/Alert";
import { Spinner } from "../components/Spinner";
import { useAsyncData } from "../hooks/useAsyncData";
import { formatError } from "../utils/formatError";
import type { ScopeCandidate, ScopeSelection } from "../api/types";

function formatDate(value: string | null): string {
  return value ? new Date(value).toLocaleString() : "never";
}

/** "site" -> "sites". Every noun the sources use pluralises this way. */
function plural(noun: string): string {
  return `${noun}s`;
}

function statusOf(scope: ScopeCandidate): { label: string; className: string } {
  if (scope.missing) {
    return { label: "No longer found", className: "badge badge--attention" };
  }
  if (scope.approved) {
    return { label: "Approved", className: "badge badge--ready" };
  }
  return { label: "Not indexed", className: "badge badge--pending" };
}

/**
 * Choosing which sites (or shared drives) an indexing run may cover.
 *
 * Only rendered when the deployment has scope selection switched on — `AppShell` hides both
 * this screen and its nav tab otherwise, because the setting would have no effect.
 */
export function ScopesScreen(): JSX.Element {
  const { data: sources } = useAsyncData(() => fetchSources(), []);
  const [source, setSource] = useState("");
  const {
    data: selection,
    loading,
    error,
    reload,
  } = useAsyncData(() => fetchScopes(source), [source]);

  const [approved, setApproved] = useState<Set<string>>(new Set());
  const [saved, setSaved] = useState<ScopeSelection | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Default to the source marked default, once the source list arrives. Empty `source` means the
  // backend picks, so the first fetch is not blocked on this.
  useEffect(() => {
    const preferred = sources?.find((entry) => entry.is_default) ?? sources?.[0];
    if (preferred && !source) {
      setSource(preferred.name);
    }
  }, [sources, source]);

  // Seed the editable set whenever a fresh copy loads from the server.
  useEffect(() => {
    if (selection) {
      setApproved(new Set(selection.scopes.filter((s) => s.approved).map((s) => s.key)));
      setSaved(selection);
      setSaveError(null);
    }
  }, [selection]);

  const toggle = (key: string): void => {
    const next = new Set(approved);
    if (!next.delete(key)) {
      next.add(key);
    }
    setApproved(next);
  };

  const handleSave = async (): Promise<void> => {
    if (!selection) {
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      // Sent from the rendered rows, so each approval carries the label the admin actually saw.
      // The server stores it, which is what lets this screen name an approved site later even
      // while discovery is failing.
      await saveScopes(
        selection.source_name,
        selection.scopes.filter((scope) => approved.has(scope.key)),
      );
      reload();
    } catch (failure) {
      setSaveError(formatError(failure));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Spinner label="Discovering…" />;
  }

  if (error || !selection) {
    return (
      <div className="scopes-screen">
        <Alert variant="error">Could not load indexing scope: {formatError(error)}</Alert>
        <button type="button" className="button button--secondary" onClick={reload}>
          Retry
        </button>
      </div>
    );
  }

  const noun = selection.scope_noun;
  const configured = (sources ?? []).filter((entry) => entry.configured);

  return (
    <div className="scopes-screen">
      <section>
        <h2>Approved {plural(noun)}</h2>
        <p>
          An indexing run covers only the {plural(noun)} ticked here. Removing one un-indexes its
          files on the next run — they stop being searchable.
        </p>

        {/* A dropdown offering one option is just noise. */}
        {configured.length > 1 && (
          <div className="run-form">
            <label htmlFor="scope-source">Source</label>
            <select
              id="scope-source"
              value={source}
              onChange={(event) => setSource(event.target.value)}
            >
              {configured.map((entry) => (
                <option key={entry.name} value={entry.name}>
                  {entry.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {selection.discovery_error && (
          <Alert variant="warning">
            Could not reach {selection.source_name} to list its {plural(noun)}, so only
            already-approved entries are shown: {selection.discovery_error}
          </Alert>
        )}

        <table className="data-table">
          <thead>
            <tr>
              <th scope="col">Indexed</th>
              <th scope="col">Name</th>
              <th scope="col">Location</th>
              <th scope="col">Status</th>
            </tr>
          </thead>
          <tbody>
            {selection.scopes.map((scope) => {
              const status = statusOf(scope);
              return (
                <tr key={scope.key}>
                  <td>
                    <input
                      type="checkbox"
                      checked={approved.has(scope.key)}
                      onChange={() => toggle(scope.key)}
                      aria-label={`Index ${scope.label || scope.key}`}
                    />
                  </td>
                  <td>{scope.label || scope.key}</td>
                  <td>{scope.detail || "—"}</td>
                  <td>
                    <span className={status.className}>{status.label}</span>
                  </td>
                </tr>
              );
            })}
            {selection.scopes.length === 0 && (
              <tr>
                <td colSpan={4}>No {plural(noun)} were found for this source.</td>
              </tr>
            )}
          </tbody>
        </table>

        {approved.size === 0 && (
          <Alert variant="warning">
            Nothing is approved, so an indexing run will be refused rather than index an empty
            corpus. Tick at least one {noun}.
          </Alert>
        )}
      </section>

      {saveError && <Alert variant="error">Could not save: {saveError}</Alert>}

      <div className="rules-screen__actions">
        <button
          type="button"
          className="button button--primary"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? "Saving…" : `Save approved ${plural(noun)}`}
        </button>
        {saved && saved.updated_at && (
          <span className="rules-screen__meta">
            Last changed by {saved.updated_by || "unknown"} at {formatDate(saved.updated_at)}
          </span>
        )}
      </div>
    </div>
  );
}
